<template>
  <transition name="fade">
    <div
      class="fixed inset-0 bg-texto-principal bg-opacity-75 flex items-center justify-center z-50"
      @click.self="$emit('close')"
    >
      <div
        class="bg-branco p-6 rounded max-w-lg w-full max-h-[90vh] overflow-y-auto relative"
      >
        <button
          class="absolute top-0.5 right-1 text-texto-secundario hover:text-texto-principal"
          :aria-label="$t('vitrine.common.close')"
          @click="$emit('close')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div class="mb-4">
          <div class="relative w-full aspect-square bg-cinza-200 rounded">
            <img
              :src="activeImage"
              :alt="
                psImageAlt
                  ? psImageAlt(product, images.indexOf(activeImage))
                  : $t('vitrine.modal.imageAlt')
              "
              class="w-full h-full object-contain cursor-pointer"
              @click="emitOpenFullscreen"
            />
            <button
              class="absolute bottom-2 right-2 flex items-center justify-center w-9 h-9 rounded-full bg-branco hover:bg-branco/90 shadow text-cinza-700"
              title="Teilen"
              aria-label="Teilen"
              @click.stop="shareProduct"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114
                     -.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632
                     3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3
                     0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0
                     00-5.368-2.684z"
                />
              </svg>
            </button>
          </div>

          <!-- barra de compartilhamento entre foto e miniaturas -->
          <transition name="fade">
            <div
              v-if="showShareBar"
              class="mt-2 mb-1 flex gap-2 flex-wrap items-center"
            >
              <button
                class="px-3 py-1 rounded bg-verde-profundo text-branco text-sm"
                @click="shareTo('whatsapp')"
              >
                WhatsApp
              </button>
              <button
                class="px-3 py-1 rounded bg-cinza-900 text-branco text-sm"
                @click="shareTo('twitter')"
              >
                X
              </button>
              <button
                class="px-3 py-1 rounded bg-azul-escuro text-branco text-sm"
                @click="shareTo('facebook')"
              >
                Facebook
              </button>
              <button
                class="px-3 py-1 rounded bg-cinza-700 text-branco text-sm"
                @click="shareTo('mail')"
              >
                E-Mail
              </button>
              <button
                class="px-3 py-1 rounded bg-cinza-200 text-cinza-900 text-sm"
                :aria-label="$t('vitrine.grid.seeDetails')"
                :title="$t('vitrine.grid.seeDetails')"
                @click="shareTo('copy')"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656
         5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0
         00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </button>
            </div>
          </transition>

          <!-- feedback abaixo da barra, igual ao grid -->
          <transition name="fade">
            <div v-if="justCopied" class="mt-1">
              <span class="text-xs text-verde-profundo">
                {{ $t("vitrine.grid.copied") }}
              </span>
            </div>
          </transition>

          <div class="flex space-x-2 mt-2">
            <div
              v-for="img in images"
              :key="img"
              class="w-16 aspect-square bg-cinza-200 rounded"
            >
              <img
                :src="img"
                :alt="
                  psImageAlt
                    ? psImageAlt(product, images.indexOf(img))
                    : $t('vitrine.modal.thumbAlt')
                "
                class="w-full h-full object-contain cursor-pointer hover:opacity-80"
                @click.stop="$emit('select-image', img)"
              />
            </div>
          </div>
        </div>

        <h2 class="text-xl font-bold mb-4">{{ product?.name }}</h2>
        <div class="space-y-2">
          <div class="flex items-center gap-2 flex-wrap">
            <strong class="shrink-0"
              >{{ $t("vitrine.product.productId") }}:</strong
            >
            <button
              type="button"
              class="flex items-center gap-1 text-sm text-texto-secundario rounded focus:outline-none focus:ring-2 focus:ring-azul-profundo"
              @click="copyProductId"
            >
              <span class="hover:underline">{{ product?.produktId }}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-cinza-500 hover:text-azul-profundo"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6
         12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2
         2 0 002 2z"
                />
              </svg>
            </button>
            <transition name="fade">
              <span
                v-if="copiedProductId"
                class="text-xs text-verde-profundo"
                aria-live="polite"
              >
                {{ $t("vitrine.grid.copied") }}
              </span>
            </transition>
          </div>
          <p>
            <strong>{{ $t("vitrine.product.brand") }}:</strong>
            {{ product?.marke?.name }}
          </p>
          <p>
            <strong>{{ $t("vitrine.product.model") }}:</strong>
            {{ product?.marke?.modell }}
          </p>
          <p>
            <strong>{{ $t("vitrine.product.color") }}:</strong>
            {{ product?.farbe }}
          </p>
          <p>
            <strong>{{ $t("vitrine.product.warranty") }}:</strong>
            {{ product?.garantieDauer }}
            {{ $t("vitrine.product.years") }}
          </p>
          <p>
            <strong>{{ $t("vitrine.product.condition") }}:</strong>
            {{ product?.zustand }}
          </p>
          <p>
            <strong>{{ $t("vitrine.product.description") }}:</strong>
            {{ product?.beschreibung }}
          </p>
          <div>
            <strong>{{ $t("vitrine.product.features") }}:</strong>
            <ul
              v-if="characteristicsList.length"
              class="mt-1 list-disc list-inside text-sm text-cinza-700"
            >
              <li
                v-for="(item, idx) in characteristicsList"
                :key="'feature-' + idx"
              >
                {{ item }}
              </li>
            </ul>
            <p v-else class="text-sm text-texto-secundario">
              {{ $t("vitrine.product.none") }}
            </p>
          </div>
          <p>
            <strong>{{ $t("vitrine.product.category") }}:</strong>
            {{
              (product?.klassifizierung?.kategorieLabel || "")
                .toUpperCase()
                .replace(/^\d{4}\s+/, "")
            }}
          </p>
          <p>
            <strong>{{ $t("vitrine.product.type") }}:</strong>
            {{ product?.klassifizierung?.typLabel?.toUpperCase() }}
          </p>
          <template v-if="product?.status !== 'Lagerbestand-Verfügbar'">
            <p v-if="discountOf(product).pct > 0">
              <strong>{{ $t("vitrine.product.price") }}: </strong>
              <span class="text-cinza-500 line-through mr-2">
                {{ formatEuro(product?.preis) }} €
              </span>
              <span class="text-vermelho-profundo font-bold">
                <span
                  v-if="product.preisAb"
                  class="text-sm font-normal text-texto-secundario mr-1"
                >
                  {{ $t("vitrine.grid.from") }}
                </span>
                {{ finalPriceOf(product) }} €
              </span>
            </p>
            <p v-else>
              <strong>{{ $t("vitrine.product.price") }}: </strong>
              <span class="text-cinza-900 font-bold">
                <span
                  v-if="product.preisAb"
                  class="text-sm font-normal text-texto-secundario mr-1"
                >
                  {{ $t("vitrine.grid.from") }}
                </span>
                {{ formatEuro(product?.preis) }} €
              </span>
            </p>
          </template>
          <template v-else>
            <p>
              <strong>{{ $t("vitrine.product.price") }}: </strong>
              <span class="text-texto-secundario">
                {{ $t("vitrine.grid.soon") }}
              </span>
            </p>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "VitrineProductModal",
  inject: ["psImageAlt"],
  props: {
    product: { type: Object, required: true },
    images: { type: Array, default: () => [] },
    selectedImage: { type: String, default: "" },
    characteristicDescriptions: { type: Array, default: () => [] },
    campaignsCat: { type: Array, default: () => [] },
    campaignsProd: { type: Array, default: () => [] },
  },
  emits: ["close", "open-fullscreen", "select-image"],
  data() {
    return {
      justCopied: false, // feedback após fallback copiar link
      showShareBar: false,
      copiedProductId: false,
    };
  },
  computed: {
    characteristicsList() {
      const source = this.characteristicDescriptions || [];
      return source
        .map((item) => String(item || "").trim())
        .filter((item) => item.length > 0);
    },
    activeImage() {
      if (this.selectedImage) return this.selectedImage;
      if (this.images && this.images.length) return this.images[0];
      return "";
    },
  },
  methods: {
    emitOpenFullscreen() {
      const idx = this.images ? this.images.indexOf(this.activeImage) : -1;
      const safeIdx = idx >= 0 ? idx : 0;
      this.$emit("open-fullscreen", safeIdx);
    },
    async copyProductId() {
      const id = this.product?.produktId || "";
      if (!id) return;
      try {
        await navigator.clipboard.writeText(id);
        this.copiedProductId = true;
        setTimeout(() => {
          this.copiedProductId = false;
        }, 2000);
      } catch (err) {
        console.error("Erro ao copiar:", err);
      }
    },

    // 🔗 Gera URL canônica: "https://.../?p=<id>"
    buildShareUrl() {
      const id = this.product?.produktId || "";
      const base = window.location.origin + "/";
      return `${base}?p=${encodeURIComponent(id)}`;
    },

    // MODIFICADO — se não houver Web Share, abre a barra
    async shareProduct() {
      const name = this.product?.name || this.$t("vitrine.grid.seeDetails");
      const id = this.product?.produktId || "";
      const url = this.buildShareUrl();
      const text = `${name} (${id})`;
      try {
        if (navigator.share) {
          await navigator.share({ title: name, text, url });
          return;
        }
      } catch (e) {
        /* segue para barra */
      }
      this.showShareBar = !this.showShareBar;
    },

    shareTo(channel) {
      const name = this.product?.name || "";
      const id = this.product?.produktId || "";
      const url = this.buildShareUrl();
      const text = `${name} (${id})`;
      const enc = encodeURIComponent;

      if (channel === "copy") {
        navigator.clipboard.writeText(url).then(() => {
          this.justCopied = true;
          setTimeout(() => (this.justCopied = false), 1600);
        });
        return;
      }

      if (channel === "whatsapp") {
        const u = `https://wa.me/?text=${enc(text + " " + url)}`;
        window.open(u, "_blank");
        return;
      }

      if (channel === "twitter") {
        const u = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(
          url
        )}`;
        window.open(u, "_blank");
        return;
      }

      if (channel === "facebook") {
        const u = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
        window.open(u, "_blank");
        return;
      }

      if (channel === "mail") {
        const u = `mailto:?subject=${enc(text)}&body=${enc(url)}`;
        window.location.href = u;
      }
    },

    // ---- helpers de preço/promo (mesma regra do grid)
    toNum(euro) {
      if (typeof euro === "number") return euro;
      const n = Number(
        String(euro)
          .replace(/[^0-9,.]/g, "")
          .replace(",", ".")
      );
      return Number.isFinite(n) ? n : 0;
    },
    formatEuro(val) {
      const n = this.toNum(val);
      return n.toFixed(2).replace(".", ",");
    },
    endOfDay(iso) {
      const s = String(iso || "").slice(0, 10);
      const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
      const dt = new Date(y || 1970, (m || 1) - 1, d || 1);
      dt.setHours(23, 59, 59, 999); // local time
      return dt.getTime();
    },
    withinWindow(startIso, endIso) {
      const start = this.endOfDay(startIso) - 24 * 60 * 60 * 1000 + 1000;
      const end = this.endOfDay(endIso);
      const now = Date.now();
      return now >= start && now <= end;
    },
    discountOf(product) {
      const grp = product?.klassifizierung?.gruppeId || "";
      const kat = product?.klassifizierung?.kategorieId || "";
      const pid = product?.produktId || "";

      const valid = (x) =>
        x &&
        x.status !== "cancelada" &&
        this.withinWindow(x.startDatum, x.endDatum);

      const prodList = (this.campaignsProd || []).filter(
        (x) => x.produktId === pid && valid(x)
      );
      const catList = (this.campaignsCat || []).filter(
        (x) => x.gruppeId === grp && x.kategorieId === kat && valid(x)
      );

      const pickMax = (arr) =>
        arr.length
          ? arr.slice().sort((a, b) => (b.prozent || 0) - (a.prozent || 0))[0]
          : null;

      const pMax = pickMax(prodList);
      const cMax = pickMax(catList);

      let chosen = null;
      if (pMax && cMax) {
        if ((cMax.prozent || 0) > (pMax.prozent || 0)) {
          chosen = { ...cMax, src: "cat" };
        } else if ((pMax.prozent || 0) > (cMax.prozent || 0)) {
          chosen = { ...pMax, src: "prod" };
        } else {
          chosen = { ...cMax, src: "cat" };
        }
      } else if (pMax) {
        chosen = { ...pMax, src: "prod" };
      } else if (cMax) {
        chosen = { ...cMax, src: "cat" };
      }

      if (!chosen) return { pct: 0, endDate: null };
      return {
        pct: Math.abs(Number(chosen.prozent || 0)),
        endDate: this.endOfDay(chosen.endDatum),
      };
    },

    finalPriceOf(product) {
      const pct = this.discountOf(product).pct;
      const base = this.toNum(product?.preis);
      const final = Math.round(base * (1 - pct / 100) * 100) / 100;
      return this.formatEuro(final);
    },
  },
};
</script>
